import React, { useMemo } from 'react';
import { Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { fetchAllShifts } from 'core/API/shifts';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import moment from 'moment-timezone';

interface PickupShiftFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	currentWeekStart?: Date;
	currentWeekEnd?: Date;
	formRef?: React.MutableRefObject<any>;
}

const validationSchema = Yup.object({
	shiftId: Yup.string().required('Please select a shift to pick up'),
	notes: Yup.string(),
});

const PickupShiftForm: React.FC<PickupShiftFormProps> = ({ 
	onSubmit, 
	onCancel: _onCancel, 
	currentWeekStart, 
	currentWeekEnd,
	formRef
}) => {
	const { workplaceId, timezones } = useWorkplacesContext();

	// Fetch house shifts for pickup request
	const { data: houseShiftsData, isLoading: houseShiftsLoading } = useQuery(
		['house-shifts', workplaceId, currentWeekStart, currentWeekEnd],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate: currentWeekStart ? moment(currentWeekStart).format('YYYY-MM-DD') : undefined,
				toDate: currentWeekEnd ? moment(currentWeekEnd).format('YYYY-MM-DD') : undefined,
			}),
		{
			enabled: !!workplaceId && !!currentWeekStart && !!currentWeekEnd,
			staleTime: 0,
			cacheTime: 0,
		}
	);

	// Filter house shifts (no assigned employee) for the selected week
	const houseShifts = useMemo(() => {
		if (!houseShiftsData?.member) return [];
		const weekStart = moment(currentWeekStart).startOf('day');
		const weekEnd = moment(currentWeekEnd).endOf('day');
		const now = moment();
		
		const filtered = houseShiftsData.member.filter((shift: any) => {
			const shiftStart = moment(shift.startAt || shift.start);
			const shiftEnd = moment(shift.endAt || shift.end);
			// Only include published unassigned shifts that fall within the selected week
			// Exclude shifts that have already ended (before current date & time)
			return (!shift.employee || shift.employee === null || shift.employee === undefined) &&
				   shiftStart.isBetween(weekStart, weekEnd, null, '[]') &&
				   shift.published === true &&
				   shiftEnd.isAfter(now);
		});
		
		// Sort by startAt first, then by jobCode label
		return filtered.sort((a: any, b: any) => {
			const startA = moment(a.startAt || a.start);
			const startB = moment(b.startAt || b.start);
			
			// Compare by start time first
			const startComparison = startA.diff(startB);
			if (startComparison !== 0) return startComparison;
			
			// If start times are equal, compare by job code label
			const labelA = a.jobCode?.label || '';
			const labelB = b.jobCode?.label || '';
			return labelA.localeCompare(labelB);
		});
	}, [houseShiftsData, currentWeekStart, currentWeekEnd]);


	const initialValues = {
		shiftId: '',
		notes: '',
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={onSubmit}
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<Box sx={{ mt: 3 }}>
						<Typography variant="h6" gutterBottom>
							Pick Up a Shift
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Week: {currentWeekStart && moment(currentWeekStart).format('MMM D')} - {currentWeekEnd && moment(currentWeekEnd).format('MMM D, YYYY')}
						</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControl fullWidth error={touched.shiftId && !!errors.shiftId}>
									<InputLabel>Select House Shift *</InputLabel>
									<Select
										value={values.shiftId}
										onChange={(e) => setFieldValue('shiftId', e.target.value)}
										label="Select House Shift *"
									>
										{houseShiftsLoading ? (
											<MenuItem value="" disabled>
												<CircularProgress size={20} color="inherit" /> Loading shifts...
											</MenuItem>
										) : houseShifts.length === 0 ? (
											<MenuItem value="" disabled>No house shifts available this week</MenuItem>
										) : (
											houseShifts.map((shift: any) => (
												<MenuItem key={shift.id} value={shift.id}>
													{moment(`${shift.startAt}`).format('ddd, MMM D - h:mm A')} - {moment(`${shift.endAt}`).format('h:mm A')} | {shift.jobCode?.label || 'Shift'}
												</MenuItem>
											))
										)}
									</Select>
									{touched.shiftId && errors.shiftId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
											{errors.shiftId}
										</Typography>
									)}
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									multiline
									rows={3}
									label="Notes (Optional)"
									value={values.notes}
									onChange={(e) => setFieldValue('notes', e.target.value)}
					placeholder="Any additional notes about picking up this shift"
				/>
			</Grid>
		</Grid>
	</Box>

	</Form>
		)}
	</Formik>
);
};

export default PickupShiftForm;